import { type InputHTMLAttributes, useId } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import { useDisconnectedState } from "@virtual-brain-lib/frontend-design-system/components";

import { Icon, type IconProps } from "./Icon";

export const checkboxVariants = tv({
	slots: {
		wrapper:
			"relative inline-flex cursor-pointer items-center rounded has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1",
		box: "inline-flex cursor-pointer items-center justify-center rounded border transition-colors duration-150 ease-in-out data-[checked=false]:border-secondary-border data-[checked=false]:bg-background",
		input: "absolute h-0 w-0 cursor-pointer opacity-0",
	},
	variants: {
		size: {
			sm: { box: "size-3.5" },
			md: { box: "size-4" },
			lg: { box: "size-5" },
		},
		color: {
			primary: {
				wrapper: "has-[:focus-visible]:outline-primary-active-ring",
				box: "data-[checked=true]:border-primary-active-ring data-[checked=true]:bg-primary-active data-[checked=true]:text-on-primary",
			},
			danger: {
				wrapper: "has-[:focus-visible]:outline-danger-active-ring",
				box: "data-[checked=true]:border-danger-active-ring data-[checked=true]:bg-danger-active data-[checked=true]:text-on-danger",
			},
			info: {
				wrapper: "has-[:focus-visible]:outline-info-active-ring",
				box: "data-[checked=true]:border-info-active-ring data-[checked=true]:bg-info-active data-[checked=true]:text-on-info",
			},
			success: {
				wrapper: "has-[:focus-visible]:outline-success-active-ring",
				box: "data-[checked=true]:border-success-active-ring data-[checked=true]:bg-success-active data-[checked=true]:text-on-success",
			},
			warning: {
				wrapper: "has-[:focus-visible]:outline-warning-active-ring",
				box: "data-[checked=true]:border-warning-active-ring data-[checked=true]:bg-warning-active data-[checked=true]:text-on-warning",
			},
		},
		disabled: {
			true: {
				wrapper: "cursor-not-allowed opacity-60",
				box: "cursor-not-allowed",
			},
		},
	},
	defaultVariants: {
		size: "md",
		color: "primary",
	},
});

type CheckboxVariants = VariantProps<typeof checkboxVariants>;

const iconSize: Record<NonNullable<CheckboxVariants["size"]>, IconProps["size"]> = {
	sm: "xs",
	md: "sm",
	lg: "md",
};

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "color"> &
	CheckboxVariants & {
		// eslint-disable-next-line react/boolean-prop-naming
		readonly checked: boolean;
		readonly onCheckedChange?: (checked: boolean) => void;
	};

export function Checkbox({
	checked,
	onCheckedChange,
	size = "md",
	color = "primary",
	disabled: propsDisabled = false,
	className,
	id: propsId,
	...props
}: CheckboxProps) {
	const { isDisconnected } = useDisconnectedState();
	const disabled = isDisconnected ?? propsDisabled;
	const { wrapper, box, input } = checkboxVariants({ size, color, disabled });

	const idRandom = useId();
	const id = propsId ?? idRandom;

	const handleChange = () => {
		if (!disabled && onCheckedChange) {
			onCheckedChange(!checked);
		}
	};

	return (
		<div className={wrapper({ className })}>
			<input
				type="checkbox"
				className={input()}
				checked={checked}
				disabled={disabled}
				id={id}
				onChange={handleChange}
				{...props}
			/>
			<label htmlFor={id} className={box()} data-checked={checked}>
				{checked && <Icon name="check" size={iconSize[size]} />}
			</label>
		</div>
	);
}
